import React from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ArrowRight } from "lucide-react";

const QA = [
  {
    q: "What is The Archive?",
    a: "An independent dossier of contested public narratives — the topics the internet argues about endlessly but rarely sources rigorously. Each file pairs a careful summary with a trail of citations you can follow yourself.",
  },
  {
    q: "Is this journalism?",
    a: "No. The Archive is editorial commentary built on top of public sources. We are not a newsroom. Treat every dossier as a starting point — never the final word.",
  },
  {
    q: "Where do the sources come from?",
    a: "Court filings, government records, primary documents, peer-reviewed work, and reporting from established outlets. Nothing appears publicly until a source has been reviewed and attached.",
  },
  {
    q: "What role does AI play?",
    a: "We use large language models as a research assistant — to summarise, structure, and cross-check material — never as a primary source. Every claim is anchored to a human-verifiable citation.",
  },
  {
    q: "Why is someone listed in The Epstein Files?",
    a: "Inclusion means there is a public, source-backed record of a relationship, transaction, document, or proximity. Inclusion is not an accusation. We do not practice guilt by association.",
  },
  {
    q: "Found an error or want to dispute a record?",
    a: "Use the submission form to file a correction or clarification. Every claim links to its source — point us at the document or filing that contradicts it and we will review.",
  },
  {
    q: "Can I republish this material?",
    a: "Editorial summaries are not for reproduction without attribution. The underlying sources belong to their respective publishers — always link back to the originals.",
  },
];

const Faq = () => (
  <div className="max-w-3xl mx-auto px-6 md:px-12 py-16" data-testid="faq-page">
    <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-3">// FREQUENTLY ASKED</div>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#E4DFD3] mb-5">
      Questions, answered plainly.
    </h1>
    <p className="text-base text-[#A39E93] leading-relaxed mb-12 max-w-2xl">
      Short answers about what The Archive is, what it isn't, and how to use it
      responsibly.
    </p>

    <div className="space-y-px bg-[#2D2A26] border border-[#2D2A26]">
      {QA.map((item, i) => (
        <details
          key={i}
          className="group bg-[#131313] open:bg-[#0F0F0F]"
          data-testid={`faq-item-${i}`}
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-start gap-3 hover:text-[#E4DFD3]">
            <HelpCircle size={14} className="text-[#D92D20] mt-1 shrink-0" />
            <span className="font-heading text-base sm:text-lg text-[#E4DFD3] flex-1">
              {item.q}
            </span>
            <span className="font-mono text-xs text-[#757065] group-open:text-[#D92D20] mt-1">
              [+]
            </span>
          </summary>
          <div className="px-5 pb-5 pl-12 text-sm text-[#A39E93] leading-relaxed">
            {item.a}
          </div>
        </details>
      ))}
    </div>

    <div className="mt-12 border border-[#2D2A26] bg-[#0F0F0F] p-6 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">
          // STILL HAVE A QUESTION
        </div>
        <p className="text-sm text-[#A39E93]">
          Use the submission form to flag a correction or ask for clarification on a record.
        </p>
      </div>
      <Link
        to="/submit?mode=correction"
        data-testid="faq-submit-btn"
        className="inline-flex items-center gap-2 border border-[#D92D20] hover:bg-[#D92D20] text-[#E4DFD3] hover:text-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em]"
      >
        Submit a correction <ArrowRight size={12} />
      </Link>
    </div>
  </div>
);

export default Faq;
